// Slider de barras con estela — el `WakeSlider` de React Bits que pasó Matías, portado a
// este repo (ADR-2026-10-03-wake-slider). Una fila de barras: las de la izquierda del valor
// van pintadas, y al arrastrar se levanta una "ola" alrededor del indicador, más alta cuanto
// más rápido se mueve y más larga detrás que adelante.
//
// Sin `motion` (no está en el proyecto): el indicador sigue al valor con un resorte
// críticamente amortiguado y la velocidad se suaviza con un filtro exponencial, los dos en
// un loop de `requestAnimationFrame` que corre sólo mientras hay movimiento y pinta las
// barras directo en el DOM (sin renders de React por cuadro). Misma física y mismas props
// que el original, salvo los colores por defecto, que son los de Atlas.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import './WakeSlider.css';

const SETTLE = 9.23;
const FULL_SPEED = 320;
const MIN_REACH = 1.5;
const FLAT = 0.002;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const smoothstep = (s: number) => s * s * (3 - 2 * s);
const toPct = (v: number, min: number, max: number) => (max > min ? ((v - min) / (max - min)) * 100 : 0);
const snap = (v: number, min: number, max: number, step: number) => {
  if (!(max > min)) return min;
  if (!(step > 0)) return clamp(v, min, max);
  const lastWhole = min + Math.floor(+((max - min) / step).toFixed(6)) * step;
  const grid = clamp(Math.round((v - min) / step) * step + min, min, lastWhole);
  return +(lastWhole < max && Math.abs(v - max) <= Math.abs(v - grid) ? max : grid).toFixed(6);
};

export interface WakeSliderProps {
  value?: number;
  defaultValue?: number;
  onChange?: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  bars?: number;
  height?: number;
  restHeight?: number;
  gap?: number;
  fillColor?: string;
  trackColor?: string;
  crestColor?: string;
  sensitivity?: number;
  reach?: number;
  skew?: number;
  glide?: number;
  smoothing?: number;
  showValue?: boolean;
  formatValue?: (value: number) => string;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
}

function useReducido(): boolean {
  const [r, setR] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const alCambiar = () => setR(mql.matches);
    alCambiar();
    mql.addEventListener('change', alCambiar);
    return () => mql.removeEventListener('change', alCambiar);
  }, []);
  return r;
}

export default function WakeSlider({
  value: valueProp,
  defaultValue = 50,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  bars = 32,
  height = 40,
  restHeight = 10,
  gap = 3,
  fillColor = 'var(--brand-primary)',
  trackColor = '#E3E6EE',
  crestColor = '',
  sensitivity = 1,
  reach = 6,
  skew = 0.6,
  glide = 0.3,
  smoothing = 100,
  showValue = false,
  formatValue,
  disabled = false,
  ariaLabel = 'Valor',
  className = '',
}: WakeSliderProps) {
  const [inner, setInner] = useState(defaultValue);
  const value = clamp(valueProp ?? inner, min, max);
  const pct = toPct(value, min, max);
  const reduce = useReducido();
  const format = formatValue ?? String;
  const rest = Math.min(restHeight, height - 1) / height;

  const rootRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<HTMLButtonElement | null>(null);
  const barEls = useRef<(HTMLSpanElement | null)[]>([]);
  const crestEls = useRef<(HTMLSpanElement | null)[]>([]);
  const pointerId = useRef<number | null>(null);
  const latest = useRef(value);
  latest.current = value;

  // El estado de la física, fuera de React: posición del indicador (en %), su velocidad
  // (%/s), la velocidad suavizada y el objetivo.
  const fisica = useRef({ x: pct, v: 0, vs: 0, objetivo: pct, amp: 0, raf: 0, t: 0 });

  const paint = useCallback(
    (force = false) => {
      const f = fisica.current;
      const h = ((reduce ? f.objetivo : f.x) / 100) * (bars - 1);
      const v = reduce ? 0 : f.vs;
      const amp = smoothstep(clamp((Math.abs(v) * sensitivity) / FULL_SPEED, 0, 1));
      const dir = Math.sign(v) || 1;
      const r = MIN_REACH + (reach - MIN_REACH) * amp;
      const behind = r * (1 + skew);
      const ahead = r * (1 - 0.5 * skew);
      const lit = Math.round(h);
      const flat = !force && amp < FLAT && f.amp < FLAT;
      for (let i = 0; i < bars; i++) {
        const el = barEls.current[i];
        if (!el) continue;
        const on = i <= lit ? 'true' : 'false';
        if (el.dataset.on !== on) el.dataset.on = on;
        if (flat) continue;
        const d = i - h;
        const R = d * dir < 0 ? behind : ahead;
        const lift = Math.abs(d) < R ? amp * Math.cos((Math.PI * d) / (2 * R)) ** 2 : 0;
        el.style.transform = `scaleY(${rest + lift * (1 - rest)})`;
        const crest = crestEls.current[i];
        if (crest) crest.style.opacity = String(lift);
      }
      f.amp = amp;
    },
    [reduce, bars, sensitivity, reach, skew, rest],
  );

  const paso = useCallback(
    (ahora: number) => {
      const f = fisica.current;
      const dt = Math.min(1 / 30, f.t ? (ahora - f.t) / 1000 : 1 / 60);
      f.t = ahora;
      // Resorte críticamente amortiguado hacia el objetivo (lo del `useSpring` original).
      const w0 = SETTLE / glide;
      const a = w0 * w0 * (f.objetivo - f.x) - 2 * w0 * f.v;
      f.v += a * dt;
      f.x += f.v * dt;
      // La velocidad que levanta la ola, suavizada (lo del `useVelocity` + `useSpring`).
      const k = 1 - Math.exp((-dt * 1000) / Math.max(1, smoothing * 0.5));
      f.vs += (f.v - f.vs) * k;
      paint();
      const quieto = Math.abs(f.objetivo - f.x) < 0.01 && Math.abs(f.v) < 0.5 && Math.abs(f.vs) < 0.5;
      if (quieto) {
        f.x = f.objetivo;
        f.v = 0;
        f.vs = 0;
        paint(true);
        f.raf = 0;
        f.t = 0;
        return;
      }
      f.raf = window.requestAnimationFrame(paso);
    },
    [glide, smoothing, paint],
  );

  const arrancar = useCallback(() => {
    const f = fisica.current;
    if (reduce) {
      f.x = f.objetivo;
      f.v = 0;
      f.vs = 0;
      paint(true);
      return;
    }
    if (!f.raf) f.raf = window.requestAnimationFrame(paso);
  }, [reduce, paint, paso]);

  useEffect(() => {
    fisica.current.objetivo = pct;
    arrancar();
  }, [pct, arrancar]);

  // Al desmontar se corta el loop y se BORRA la marca de que corre: en StrictMode React
  // monta, desmonta y vuelve a montar, y con el id viejo guardado `arrancar` creía que el
  // loop seguía vivo y no lo volvía a lanzar — las barras se pintaban pero nunca había ola.
  useEffect(
    () => () => {
      const f = fisica.current;
      if (f.raf) window.cancelAnimationFrame(f.raf);
      f.raf = 0;
      f.t = 0;
    },
    [],
  );

  useLayoutEffect(() => {
    paint(true);
  });

  const commit = (next: number) => {
    const clean = snap(next, min, max, step);
    if (clean === latest.current) return;
    latest.current = clean;
    if (valueProp === undefined) setInner(clean);
    onChange?.(clean);
  };
  const commitFromX = (x: number) => {
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    if (!rect.width) return;
    let ratio = clamp((x - rect.left) / rect.width, 0, 1);
    if (getComputedStyle(track).direction === 'rtl') ratio = 1 - ratio;
    commit(min + ratio * (max - min));
  };
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (disabled || pointerId.current !== null) return;
    pointerId.current = e.pointerId;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
    rootRef.current?.removeAttribute('data-instant');
    handleRef.current?.focus({ preventScroll: true });
    commitFromX(e.clientX);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerId === pointerId.current) commitFromX(e.clientX);
  };
  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerId !== pointerId.current) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
    pointerId.current = null;
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    const jumps: Record<string, number> = {
      ArrowRight: value + step,
      ArrowUp: value + step,
      ArrowLeft: value - step,
      ArrowDown: value - step,
      PageUp: value + step * 10,
      PageDown: value - step * 10,
      Home: min,
      End: max,
    };
    if (!(e.key in jumps)) return;
    e.preventDefault();
    const clean = snap(jumps[e.key], min, max, step);
    const p = toPct(clean, min, max);
    // Con el teclado, sin ola: el indicador salta.
    rootRef.current?.setAttribute('data-instant', 'true');
    const f = fisica.current;
    f.x = p;
    f.v = 0;
    f.vs = 0;
    f.objetivo = p;
    commit(clean);
  };

  const barNodes = useMemo(() => {
    barEls.current.length = bars;
    crestEls.current.length = bars;
    return Array.from({ length: bars }, (_, i) => (
      <span
        key={i}
        ref={(el) => {
          barEls.current[i] = el;
        }}
        className="wake-slider__bar"
      >
        {crestColor ? (
          <span
            ref={(el) => {
              crestEls.current[i] = el;
            }}
            className="wake-slider__crest"
          />
        ) : null}
      </span>
    ));
  }, [bars, crestColor]);

  return (
    <div
      ref={rootRef}
      className={`wake-slider${className ? ` ${className}` : ''}`}
      aria-disabled={disabled || undefined}
      style={
        {
          '--ws-fill': fillColor,
          '--ws-track': trackColor,
          '--ws-crest': crestColor || fillColor,
          '--ws-height': `${height}px`,
          '--ws-gap': `${gap}px`,
          '--ws-rest': rest,
        } as React.CSSProperties
      }
    >
      <div
        ref={trackRef}
        className="wake-slider__track"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onLostPointerCapture={endDrag}
      >
        {barNodes}
        <button
          ref={handleRef}
          type="button"
          role="slider"
          className="wake-slider__handle"
          tabIndex={disabled ? -1 : 0}
          aria-label={ariaLabel}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          aria-valuetext={format(value)}
          aria-disabled={disabled || undefined}
          onKeyDown={onKeyDown}
        />
      </div>
      {showValue ? (
        <span className="wake-slider__value" aria-hidden="true">
          {format(value)}
        </span>
      ) : null}
    </div>
  );
}
